import type {
    TutorEngine,
    TutorExplanation,
    TutorExplanationRequest,
    TutorSourceBlock
} from "./TutorEngine.js";

const titleByLanguage = {
    fr: "L’essentiel de cette carte",
    fa: "نکته‌های اصلی این بخش"
} as const;

function uniqueBlocks(
    blocks: readonly TutorSourceBlock[]
): readonly TutorSourceBlock[] {
    const seen = new Set<string>();

    return blocks.filter(block => {
        const normalized = block.text.trim();

        if (!normalized || seen.has(normalized)) {
            return false;
        }

        seen.add(normalized);
        return true;
    });
}

function selectSummary(
    blocks: readonly TutorSourceBlock[]
): TutorSourceBlock | undefined {
    return blocks.find(block => block.kind === "paragraph")
        ?? blocks.find(block => block.kind === "note")
        ?? blocks.find(block => block.kind !== "heading")
        ?? blocks[0];
}

class CanonicalTutorEngine implements TutorEngine {
    async explain(
        request: TutorExplanationRequest
    ): Promise<TutorExplanation> {
        const blocks = uniqueBlocks(request.source.blocks);
        const summaryBlock = selectSummary(blocks);

        if (!summaryBlock) {
            throw new Error("The selected learning surface has no canonical content.");
        }

        const points = blocks
            .filter(block => block !== summaryBlock && block.kind !== "heading")
            .slice(0, 3)
            .map(block => block.text);

        return {
            mode: "canonical",
            title: titleByLanguage[request.language],
            summary: summaryBlock.text,
            points,
            sourceLabel: request.source.label
        };
    }
}

const canonicalTutorEngine = new CanonicalTutorEngine();

export {
    CanonicalTutorEngine,
    canonicalTutorEngine
};
